import {
  AutocompleteInput,
  Datagrid,
  FunctionField,
  List,
  NumberField,
  Pagination,
  ReferenceInput,
  SelectInput,
  TextField,
} from 'react-admin'
import { Chip, Link, Typography } from '@mui/material'

import FilterComponent from '../../components/Filter'

import {
  CHAPTER_DISCUSSION_COMMENTS_RESOURCE,
  type DiscussionCommentListRecord,
} from './chapterDiscussions.dataProvider'

export type DiscussionModerationListProps = Record<string, never>

const MODERATION_CHOICES = [
  'queued',
  'approved',
  'rejected',
  'needs_review',
  'error',
  'rate_limited',
  'invalid',
].map(status => ({ id: status, name: status }))

export const VISIBILITY_CHOICES = ['visible', 'hidden', 'deleted'].map(
  status => ({ id: status, name: status }),
)

const POSITIVE_STATUSES = ['approved', 'visible']
const NEGATIVE_STATUSES = ['deleted', 'hidden', 'rejected']

const POSITIVE_COLOR = '#58A700'
const NEGATIVE_COLOR = '#F96262'

/** Returns Chip styles tinting positive/negative statuses, or undefined for neutral ones. */
export function statusChipSx(status: string) {
  if (POSITIVE_STATUSES.includes(status)) {
    return { color: POSITIVE_COLOR, backgroundColor: `${POSITIVE_COLOR}1A` }
  }
  if (NEGATIVE_STATUSES.includes(status)) {
    return { color: NEGATIVE_COLOR, backgroundColor: `${NEGATIVE_COLOR}1A` }
  }
  return undefined
}

export const bookTitleFilterToQuery = (searchText: string) => ({
  title: { $iLike: `%${searchText}%` },
})

export const authorNameFilterToQuery = (searchText: string) => ({
  name: { $iLike: `%${searchText}%` },
})

const DiscussionPagination = () => (
  <Pagination rowsPerPageOptions={[20, 50, 100]} />
)

const DiscussionModerationFilter = (props: Record<string, unknown>) => (
  <FilterComponent styles={{}} {...props}>
    <ReferenceInput label="Book" source="bookId" reference="books" alwaysOn>
      <AutocompleteInput
        label="Book"
        optionText="title"
        filterToQuery={bookTitleFilterToQuery}
      />
    </ReferenceInput>
    <ReferenceInput label="Author" source="authorId" reference="users" alwaysOn>
      <AutocompleteInput
        label="Author"
        optionText="name"
        filterToQuery={authorNameFilterToQuery}
      />
    </ReferenceInput>
    <SelectInput
      source="moderationStatus"
      label="Moderation"
      choices={MODERATION_CHOICES}
      alwaysOn
    />
    <SelectInput
      source="visibilityStatus"
      label="Visibility"
      choices={VISIBILITY_CHOICES}
      alwaysOn
    />
  </FilterComponent>
)

export function discussionCommentRowClick(
  record: DiscussionCommentListRecord,
): string | false {
  if (window.getSelection()?.toString().trim()) return false
  return `/chapter-discussions/${record.id}/show`
}

export function DiscussionModerationList() {
  return (
    <List
      resource={CHAPTER_DISCUSSION_COMMENTS_RESOURCE}
      title={false}
      actions={false}
      filters={<DiscussionModerationFilter />}
      pagination={<DiscussionPagination />}
      perPage={20}
      sort={{ field: 'createdAt', order: 'DESC' }}
      storeKey="chapter-discussion-comments-list">
      <Datagrid
        bulkActionButtons={false}
        rowClick={(_id, _resource, record) =>
          discussionCommentRowClick(record as DiscussionCommentListRecord)
        }>
        <FunctionField<DiscussionCommentListRecord>
          label="Date"
          source="createdAt"
          sortable={false}
          sx={{ minWidth: 180, whiteSpace: 'nowrap' }}
          render={record => new Date(record.createdAt).toLocaleString()}
        />
        <TextField
          source="bookTitle"
          label="Book"
          sortable={false}
          sx={{ minWidth: 260, display: 'inline-block' }}
        />
        <TextField source="chapterTitle" label="Chapter" sortable={false} />
        <FunctionField<DiscussionCommentListRecord>
          label="Comment / Rely"
          source="commentBody"
          sortable={false}
          sx={{ maxWidth: 360 }}
          render={record => (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {record.commentBody}
            </Typography>
          )}
        />
        <FunctionField<DiscussionCommentListRecord>
          label="Commenter"
          source="authorName"
          sortable={false}
          render={record => (
            <Link
              href={`#/user-details/${record.authorId}/show`}
              sx={{ color: '#F47D42' }}
              onClick={event => event.stopPropagation()}>
              {record.authorName}
            </Link>
          )}
        />
        <NumberField source="reportCount" label="Reports" sortable={false} />
        <FunctionField<DiscussionCommentListRecord>
          label="Moderation"
          source="moderationStatus"
          sortable={false}
          render={record => (
            <Chip
              size="small"
              label={record.moderationStatus}
              sx={statusChipSx(record.moderationStatus)}
            />
          )}
        />
        <FunctionField<DiscussionCommentListRecord>
          label="Visibility"
          source="visibilityStatus"
          sortable={false}
          render={record => (
            <Chip
              size="small"
              label={record.visibilityStatus}
              sx={statusChipSx(record.visibilityStatus)}
            />
          )}
        />
      </Datagrid>
    </List>
  )
}
